import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from 'hooks/useAuth';
import { analyzeIncident } from 'services/api';
import { MOCK_INCIDENTS, MOCK_RESPONDERS, SEVERITY_LEVELS, INCIDENT_CATEGORIES, DISPATCH_STATUS_CONFIG } from 'utils/mockData';
import { formatTimeAgo } from 'utils/helpers';
import IncidentMap from 'components/common/IncidentMap';
import ConfidenceMeter from 'components/common/ConfidenceMeter';
import DispatchChain from 'components/common/DispatchChain';
import { SkeletonCard } from 'components/common/Skeleton';
import { respondToDispatch, initDispatch } from 'services/dispatchStore';
import toast from 'react-hot-toast';

// Simulate current logged-in responder
const CURRENT_RESPONDER_ID = 'resp-p1'; // In prod, this comes from userProfile

function MissionBrief({ incident, loading, brief }) {
  if (loading) return <SkeletonCard lines={6} />;
  if (!brief) return (
    <div className="glass-card p-5 text-center text-gray-400 text-sm">
      <div className="text-3xl mb-2">🎯</div>
      Accept a mission to receive AI tactical brief
    </div>
  );
  const cat = INCIDENT_CATEGORIES[incident?.type] || {};
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5 space-y-4" style={{ borderColor: cat.border || '#FF3B3B30' }}>
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-sm" style={{ color: cat.color }}>🧠 AI Mission Brief</h3>
        <span className="text-xs font-mono text-gray-400">Gemini · {incident?.subcategory}</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-center">
          <div className="font-display font-black text-3xl" style={{ color: cat.color }}>{brief.threatLevel}</div>
          <div className="text-xs text-gray-400">Threat /10</div>
        </div>
        <div className="text-sm text-gray-300 flex-1">{brief.riskAssessment}</div>
      </div>
      <div>
        <div className="text-xs font-mono text-amber mb-2">⚡ IMMEDIATE ACTIONS</div>
        <ul className="space-y-1">
          {(brief.immediateActions || []).map((a, i) => (
            <li key={i} className="text-sm text-gray-300 flex gap-2"><span className="text-electric">{i + 1}.</span>{a}</li>
          ))}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-navy rounded-lg p-3">
          <div className="text-xs font-mono text-gray-400 mb-1">🛠️ Equipment</div>
          {(brief.equipmentNeeded || []).slice(0, 3).map((eq, i) => <div key={i} className="text-xs text-gray-300">• {eq}</div>)}
        </div>
        <div className="bg-navy rounded-lg p-3">
          <div className="text-xs font-mono text-gray-400 mb-1">⏱ Duration</div>
          <div className="text-sm font-bold text-white">{brief.estimatedDuration || '30-60 min'}</div>
          <div className="text-xs text-gray-400 mt-1">Evac: {brief.evacuationRadius || 0}m radius</div>
        </div>
      </div>
      {brief.specialConsiderations && (
        <div className="p-3 rounded-lg bg-amber/10 border border-amber/20">
          <div className="text-xs font-mono text-amber mb-1">⚠️ Special Considerations</div>
          <div className="text-xs text-gray-300">{brief.specialConsiderations}</div>
        </div>
      )}
    </motion.div>
  );
}

function IncidentCard({ incident, onSelect, isSelected, myDispatch }) {
  const cat = INCIDENT_CATEGORIES[incident.type] || {};
  const sev = SEVERITY_LEVELS[incident.severity] || {};
  const dispCfg = myDispatch ? DISPATCH_STATUS_CONFIG[myDispatch.status] : null;

  return (
    <motion.div
      layout
      whileHover={{ scale: 1.01 }}
      onClick={() => onSelect(incident)}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-electric shadow-glow-blue' : ''}`}
      style={{ borderColor: isSelected ? '#1E90FF' : cat.border || '#1E2A45', background: cat.bg || '#141B2D' }}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0 mt-0.5">{cat.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-sm text-white">{incident.subcategory}</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span>
            {incident.priorityScore >= 9 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-crimson/20 text-crimson font-bold animate-pulse">PRIORITY</span>
            )}
          </div>
          <div className="text-xs text-gray-400 mb-1 truncate">📍 {incident.location.address}</div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>👥 {incident.peopleAffected} affected</span>
            <span>·</span>
            <span>{formatTimeAgo(incident.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ConfidenceMeter score={incident.verificationScore} size={40} label="" />
          <span className="text-xs font-mono font-bold" style={{ color: sev.color }}>{incident.priorityScore}/10</span>
        </div>
      </div>

      {/* My dispatch status banner */}
      {myDispatch && dispCfg && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: dispCfg.color + '15', border: `1px solid ${dispCfg.color}30` }}>
          <span>{dispCfg.icon}</span>
          <span className="text-xs font-semibold" style={{ color: dispCfg.color }}>Your status: {dispCfg.label}</span>
          {myDispatch.status === 'standby' && (
            <span className="text-xs text-gray-400 ml-1">— Ready to activate if #1 declines</span>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function ResponderDashboard() {
  const { userProfile } = useAuth();
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [myAccepted, setMyAccepted] = useState(null); // The incident I accepted

  // Initialize dispatch for all incidents
  useEffect(() => {
    MOCK_INCIDENTS.forEach((inc) => initDispatch(inc.id, inc.dispatchChain || []));
  }, []);

  // Get incidents relevant to me (where I'm in dispatch chain)
  const myIncidents = MOCK_INCIDENTS.filter((inc) =>
    inc.dispatchChain?.some((d) => d.responderId === CURRENT_RESPONDER_ID)
  );

  // All active incidents sorted by priority
  const filteredIncidents = MOCK_INCIDENTS
    .filter((i) => i.status !== 'resolved')
    .filter((i) => filterType === 'all' || i.type === filterType)
    .sort((a, b) => b.priorityScore - a.priorityScore);

  const getMyDispatch = (incident) =>
    incident.dispatchChain?.find((d) => d.responderId === CURRENT_RESPONDER_ID);

  const handleSelectIncident = async (incident) => {
    if (selectedIncident?.id === incident.id) { setSelectedIncident(null); setBrief(null); return; }
    setSelectedIncident(incident);
    setBrief(null);
    setLoadingBrief(true);
    try {
      const res = await analyzeIncident({ incidentId: incident.id, incidentData: incident });
      setBrief(res.data);
    } catch {
      setBrief({
        threatLevel: incident.priorityScore,
        immediateActions: [
          'Secure and assess the scene on arrival',
          'Establish contact with dispatch and incident commander',
          'Coordinate with other units already on scene',
          'Document all actions and timings',
        ],
        equipmentNeeded: ['Standard response kit', 'Communication radio', 'First aid supplies'],
        estimatedDuration: incident.type === 'police' ? '1-3 hours' : '30-90 min',
        evacuationRadius: incident.aiAnalysis?.evacuationRadius || 0,
        riskAssessment: incident.aiAnalysis?.riskAssessment || `${incident.severity.toUpperCase()} incident — ${incident.peopleAffected} affected.`,
        specialConsiderations: incident.type === 'police' && incident.subcategory === 'Kidnapping'
          ? 'AMBER Alert protocol. Do not engage suspect directly without backup.'
          : incident.type === 'police' && incident.subcategory === 'Riot / Hangama'
          ? 'Riot gear required. Do not enter crowd without 3+ unit backup.'
          : 'Follow standard operating procedures. Report any escalation immediately.',
      });
    } finally {
      setLoadingBrief(false);
    }
  };

  const handleDispatchAction = (action, responderId) => {
    if (responderId === CURRENT_RESPONDER_ID && action === 'accept') {
      setMyAccepted(selectedIncident);
      toast.success('Mission accepted — check map for navigation', { icon: '🚀' });
    }
  };

  return (
    <div className="min-h-screen bg-navy">
      {/* My active incidents banner */}
      {myIncidents.length > 0 && (
        <div className="border-b border-amber/20 bg-amber/5 px-4 py-2">
          <div className="flex items-center gap-2 text-xs text-amber font-semibold">
            <span className="animate-pulse">🔔</span>
            <span>You have {myIncidents.length} incident{myIncidents.length > 1 ? 's' : ''} requiring your response:</span>
            {myIncidents.map((i) => (
              <button key={i.id} onClick={() => handleSelectIncident(i)} className="underline hover:no-underline">
                {i.subcategory}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-4 py-4 border-b border-navy-border flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-base text-white">Responder Command</h1>
          <p className="text-xs text-gray-400">
            {MOCK_RESPONDERS.find((r) => r.id === CURRENT_RESPONDER_ID)?.name} ·{' '}
            {MOCK_RESPONDERS.find((r) => r.id === CURRENT_RESPONDER_ID)?.role} ·{' '}
            {filteredIncidents.length} active incidents
          </p>
        </div>
        <div className="px-3 py-1.5 rounded-full bg-emerald/10 border border-emerald/30 text-xs font-mono text-emerald">
          🟢 Available
        </div>
      </div>

      {/* Type filter */}
      <div className="px-4 py-3 border-b border-navy-border flex gap-2 overflow-x-auto">
        <button
          onClick={() => setFilterType('all')}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filterType === 'all' ? 'bg-electric text-navy' : 'bg-navy-card border border-navy-border text-gray-400 hover:text-white'}`}
        >
          All Types
        </button>
        {Object.entries(INCIDENT_CATEGORIES).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setFilterType(key)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filterType === key ? 'text-navy' : 'border border-navy-border text-gray-400 hover:text-white'}`}
            style={filterType === key ? { background: cfg.color } : {}}
          >
            <span>{cfg.icon}</span> {cfg.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-160px)]">
        {/* Left: Incident List */}
        <div className="w-full lg:w-2/5 border-r border-navy-border overflow-y-auto">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-xs text-gray-400 uppercase tracking-wider">Active Incidents</h2>
              <span className="text-xs font-mono text-gray-400">Sorted by priority</span>
            </div>
            {filteredIncidents.map((inc) => (
              <IncidentCard
                key={inc.id}
                incident={inc}
                onSelect={handleSelectIncident}
                isSelected={selectedIncident?.id === inc.id}
                myDispatch={getMyDispatch(inc)}
              />
            ))}
            {filteredIncidents.length === 0 && (
              <div className="text-center py-10 text-gray-400">
                <div className="text-3xl mb-2">✅</div>
                No active incidents for this type
              </div>
            )}
          </div>
        </div>

        {/* Right: Map + Brief + Dispatch Chain */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Map */}
          <IncidentMap
            incidents={selectedIncident ? [selectedIncident] : filteredIncidents}
            userLocation={MOCK_RESPONDERS.find((r) => r.id === CURRENT_RESPONDER_ID)?.location}
            height="260px"
          />

          {/* Dispatch Chain for selected incident */}
          {selectedIncident && (
            <DispatchChain
              incident={selectedIncident}
              currentResponderId={CURRENT_RESPONDER_ID}
              onActionComplete={handleDispatchAction}
            />
          )}

          {/* AI Mission Brief */}
          <MissionBrief incident={selectedIncident} loading={loadingBrief} brief={brief} />

          {!selectedIncident && (
            <div className="glass-card p-8 text-center text-gray-400">
              <div className="text-4xl mb-3">🎯</div>
              <div className="font-display font-bold text-sm text-white mb-1">Select an Incident</div>
              <div className="text-sm">Click any incident card to view dispatch chain, AI mission brief, and map route</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
